"use client";

import { Bars2Icon } from "@heroicons/react/24/outline";
import { Search } from "lucide-react";
import Link from "next/link";

import AccountMenu from "@/components/nav/AccountMenu";
import HelpButton from "@/components/nav/HelpButton";
import SabineLead from "@/components/SabineLead";
import { NAV_ICON_BUTTON } from "@/components/nav/nav-styles";
import { WorkspaceLogo } from "@/components/nav/WorkspaceSwitcher";
import { SABINE_NAME } from "@/lib/sabine";
import { openNavSearch } from "@/lib/navigation/nav-search-open";
import { cn } from "@/lib/utils";

/**
 * The cream headers SideNav puts where the navy top bar is not: a signed-out
 * page's, and a phone's (below `md`, with the drawer's menu button).
 */

const StudioLogo = ({ src, name }) => (
  <WorkspaceLogo workspace={{ name, image: src }} compact />
);

/** Signed-out pages keep a plain top bar: the studio's mark and name. */
export function SignedOutBar({ displayName, displayLogo, onOpenCoach, inert }) {
  return (
    <header
      inert={inert}
      className="flex items-center gap-3 bg-surface-chrome px-4 py-2.5 md:px-6"
    >
      <Link href="/" className="flex min-w-0 items-center gap-3">
        <StudioLogo src={displayLogo} name={displayName} />
        <span className="truncate text-lg font-bold text-gray-700">
          {displayName || "Salesably — Execution Excellence for Sales Teams"}
        </span>
      </Link>
      {/* Below `md` the strip is hidden, so the coach opens from here. */}
      {onOpenCoach && (
        <CoachIconButton onClick={onOpenCoach} className="ml-auto md:hidden" />
      )}
    </header>
  );
}

/** Sabine's face as an icon button: the coach's opener where the strip is not. */
const CoachIconButton = ({ onClick, className }) => (
  <button
    type="button"
    aria-label={`Ask ${SABINE_NAME}`}
    onClick={onClick}
    className={cn(NAV_ICON_BUTTON, className)}
  >
    <SabineLead size="sm" />
  </button>
);

export function MobileHeader({
  logoSrc,
  displayName,
  user,
  menuOpen,
  onOpenMenu,
  onOpenCoach,
  help,
  inert,
}) {
  return (
    <header
      inert={inert}
      data-app-nav
      className="flex items-center justify-between gap-3 px-4 py-2 md:hidden print:hidden"
    >
      <Link href="/" className="flex min-w-0 items-center gap-2">
        <StudioLogo src={logoSrc} name={displayName} />
        <span className="truncate text-sm font-semibold text-gray-900">
          {displayName}
        </span>
      </Link>
      <div className="flex items-center gap-1">
        {onOpenCoach && <CoachIconButton onClick={onOpenCoach} />}
        {help && <HelpButton compact open={help.open} onClick={help.onClick} />}
        <button
          type="button"
          aria-label="Search"
          onClick={() => openNavSearch()}
          className={NAV_ICON_BUTTON}
        >
          <Search className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={menuOpen}
          onClick={onOpenMenu}
          className={NAV_ICON_BUTTON}
        >
          <Bars2Icon className="h-6 w-6" />
        </button>
        <AccountMenu user={user} side="bottom" />
      </div>
    </header>
  );
}
